/**
 * "Novo recado" — cinco campos, três opcionais.
 *
 * O único obrigatório é o texto: se a secretária estiver com o telefone na
 * orelha e só conseguir digitar a frase, o post-it existe assim mesmo. O
 * paciente é opcional de propósito (ver `MuralNote`), mas o formulário deixa
 * claro o que se perde sem ele.
 */
import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { PatientTypeahead } from '@/components/patients/PatientTypeahead';
import { CATEGORY_META } from './muralUi';
import { MURAL_CATEGORIES } from '@/types/models';
import type { MuralCategory, Patient } from '@/types/models';
import type { NewMuralNote } from '@/services/firebase/muralNotes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clinicId: string;
  /** Paciente já escolhido na busca do mural — evita digitar o nome de novo. */
  initialPatient?: Patient | null;
  /** Texto já preenchido — ex.: mensagem do chat virando post-it. */
  initialText?: string;
  /** Linha acima do formulário explicando de onde veio o recado. */
  sourceHint?: string;
  onCreate: (data: NewMuralNote) => Promise<void>;
}

export const NewMuralNoteModal = ({
  isOpen,
  onClose,
  clinicId,
  initialPatient,
  initialText,
  sourceHint,
  onCreate,
}: Props) => {
  const [patient, setPatient] = useState<Patient | null>(initialPatient ?? null);
  const [text, setText] = useState('');
  const [category, setCategory] = useState<MuralCategory>('outro');
  const [status, setStatus] = useState<'anotado' | 'aguardando'>('anotado');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPatient(initialPatient ?? null);
      setText(initialText ?? '');
      setCategory('outro');
      setStatus('anotado');
    }
  }, [isOpen, initialPatient, initialText]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onCreate({
        text: trimmed,
        category,
        status,
        patientId: patient?.id,
        patientName: patient ? patient.socialName || patient.name : undefined,
        patientNumber: patient?.patientNumber,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo recado" maxWidth="md">
      <div className="space-y-4">
        {sourceHint && (
          <p className="rounded-md bg-surface-container px-2.5 py-1.5 text-xs text-on-surface-variant">
            {sourceHint}
          </p>
        )}
        <div>
          <PatientTypeahead
            clinicId={clinicId}
            value={patient}
            onChange={setPatient}
            label="Paciente (opcional)"
          />
          {!patient && (
            <p className="mt-1 text-xs text-on-surface-variant">
              Sem paciente o recado fica no mural, mas não aparece em busca por nome/CPF nem no
              histórico de ficha nenhuma.
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-on-surface-variant">
            Recado
          </label>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
            }}
            rows={3}
            placeholder="O que o paciente pediu, na fala dele…"
            className="w-full resize-none rounded-md border border-outline-variant/40 bg-surface-container-lowest p-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-on-surface-variant">
            Categoria
          </label>
          <div className="flex flex-wrap gap-2">
            {MURAL_CATEGORIES.map((c) => {
              const meta = CATEGORY_META[c];
              const on = category === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  aria-pressed={on}
                  className={`rounded border px-3 py-1.5 text-xs font-medium transition-colors ${meta.paper} ${
                    on ? 'ring-2 ring-primary ring-offset-1 ring-offset-surface-container-low' : 'opacity-70 hover:opacity-100'
                  } ${meta.ink}`}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-on-surface-variant">
            Coluna
          </label>
          <div className="flex flex-wrap gap-2">
            {(['anotado', 'aguardando'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                aria-pressed={status === s}
                className={`rounded border px-3 py-1.5 text-xs font-medium transition-colors ${
                  status === s
                    ? 'border-primary bg-primary/15 text-on-surface'
                    : 'border-outline-variant/40 text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                {s === 'anotado' ? 'Anotado' : 'Aguardando'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!text.trim()} isLoading={saving}>
            Colar no mural
          </Button>
        </div>
      </div>
    </Modal>
  );
};
